import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import AppDialog from "../General/Dialog/AppDialog";
import GlassCard from "../General/GlassCard";
import DatagridCustom from "../General/DatagridCustom";
import PartyAutocomplete from "./PartyAutocomplete";
import {
  getClient,
  getClientLegalFiles,
  attachPartyToClient,
  detachPartyFromClient,
} from "../../services/ClientService";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";

export default function ClientDetailDialog({ open, onClose, clientId, onChanged }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedToLink, setSelectedToLink] = useState([]);
  const [linking, setLinking] = useState(false);

  const [legalFilesRows, setLegalFilesRows] = useState([]);
  const [legalFilesTotal, setLegalFilesTotal] = useState(0);
  const [legalFilesLoading, setLegalFilesLoading] = useState(false);
  const [legalFilesPagination, setLegalFilesPagination] = useState({ page: 0, pageSize: 20 });
  const [legalFilesFilterModel, setLegalFilesFilterModel] = useState({ items: [], quickFilterValues: [] });

  const canLink = hasPermission("vincular parte cliente");

  const fetchClient = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await getClient(clientId);
      setClient(res.data?.data || null);
    } catch {
      showToast("No se pudo cargar el cliente.", "error");
    } finally {
      setLoading(false);
    }
  }, [clientId, showToast]);

  const fetchLegalFiles = useCallback(async () => {
    if (!open || !clientId) return;
    setLegalFilesLoading(true);
    try {
      const { page, pageSize } = legalFilesPagination;
      const searchTerm = legalFilesFilterModel.quickFilterValues?.join(" ") || "";
      const res = await getClientLegalFiles(clientId, {
        page: page + 1,
        perPage: pageSize,
        search: searchTerm,
      });
      setLegalFilesRows(res.data?.data || []);
      setLegalFilesTotal(res.data?.total || 0);
    } catch {
      showToast("No se pudieron cargar los expedientes.", "error");
    } finally {
      setLegalFilesLoading(false);
    }
  }, [open, clientId, legalFilesPagination, legalFilesFilterModel, showToast]);

  useEffect(() => {
    if (open) {
      fetchClient();
      setSelectedToLink([]);
      setLegalFilesPagination({ page: 0, pageSize: 20 });
      setLegalFilesFilterModel({ items: [], quickFilterValues: [] });
    }
  }, [open, fetchClient]);

  useEffect(() => {
    fetchLegalFiles();
  }, [fetchLegalFiles]);

  const handleLegalFilesFilterChange = useCallback(
    (newFilterModel) => {
      const currentSearchTerm = legalFilesFilterModel.quickFilterValues?.join(" ") || "";
      const newSearchTerm = newFilterModel.quickFilterValues?.join(" ") || "";

      setLegalFilesFilterModel(newFilterModel);

      if (currentSearchTerm !== newSearchTerm) {
        setLegalFilesPagination((prev) => (prev.page !== 0 ? { ...prev, page: 0 } : prev));
      }
    },
    [legalFilesFilterModel],
  );

  const handleLinkSelected = async () => {
    if (!selectedToLink.length) return;
    setLinking(true);
    try {
      await Promise.all(
        selectedToLink.map((p) => attachPartyToClient(clientId, p.id)),
      );
      showToast(
        `${selectedToLink.length} parte(s) vinculada(s) correctamente.`,
        "success",
      );
      setSelectedToLink([]);
      await fetchClient();
      onChanged?.();
    } catch {
      showToast("No se pudieron vincular todas las partes seleccionadas.", "error");
    } finally {
      setLinking(false);
    }
  };

  const handleDetach = async (party) => {
    try {
      await detachPartyFromClient(clientId, party.id);
      showToast("Parte desvinculada.", "success");
      await fetchClient();
      onChanged?.();
    } catch {
      showToast("No se pudo desvincular la parte.", "error");
    }
  };

  const legalFilesColumns = [
    { field: "code", headerName: "Código", flex: 1, minWidth: 140 },
    { field: "name", headerName: "Nombre", flex: 1.5, minWidth: 200 },
    { field: "status", headerName: "Estado", flex: 1, minWidth: 130 },
    { field: "location", headerName: "Ubicación", flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: "Detalle",
      sortable: false,
      filterable: false,
      width: 100,
      renderCell: (params) => (
        <Tooltip title="Abrir expediente en otra pestaña">
          <IconButton
            size="small"
            component="a"
            href={`/legal/file/${params.row.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={client ? client.name : "Cliente"}
      subtitle={client?.document || ""}
      icon={<BadgeRoundedIcon />}
      maxWidth="lg"
      fullWidth
      fullScreenMobile
      fullScreenBreakpoint="md"
      stickyHeader
      contentSx={{ p: { xs: 1.2, sm: 2 } }}
    >
      {loading || !client ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={`${client.parties?.length ?? 0} parte(s) vinculada(s)`}
              size="small"
              sx={{ fontWeight: 800, bgcolor: "rgba(25,118,210,0.10)", color: "primary.main" }}
            />
            <Chip
              label={`${client.legal_files_count ?? 0} expediente(s)`}
              size="small"
              sx={{ fontWeight: 800, bgcolor: "rgba(46,125,50,0.10)", color: "success.main" }}
            />
          </Box>

          {/* Partes vinculadas */}
          <GlassCard>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>Partes vinculadas</Typography>
            {!client.parties?.length ? (
              <Typography variant="body2" sx={{ opacity: 0.65 }}>
                Todavía no hay partes vinculadas a este cliente.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {client.parties.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 0.75,
                      px: 1,
                      borderRadius: 2,
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {`${p.name || ""} ${p.lastname || ""}`.trim()}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.65 }}>
                        {p.role || "—"} · {p.document || "sin documento"} ·{" "}
                        {p.legal_files_count ?? 0} expediente(s)
                      </Typography>
                    </Box>

                    {canLink && (
                      <Tooltip title="Desvincular de este cliente">
                        <IconButton size="small" color="error" onClick={() => handleDetach(p)}>
                          <PersonRemoveRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </GlassCard>

          {/* Buscar y vincular parte */}
          {canLink && (
            <GlassCard>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Vincular parte</Typography>
              <PartyAutocomplete
                value={selectedToLink}
                onChange={setSelectedToLink}
                excludeIds={(client.parties || []).map((p) => p.id)}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.2 }}>
                <Button
                  variant="contained"
                  onClick={handleLinkSelected}
                  disabled={!selectedToLink.length || linking}
                  startIcon={
                    linking ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PersonAddRoundedIcon />
                    )
                  }
                  sx={{ borderRadius: 2, fontWeight: 800 }}
                >
                  Vincular{selectedToLink.length > 0 ? ` (${selectedToLink.length})` : ""}
                </Button>
              </Box>
            </GlassCard>
          )}

          <Divider />

          {/* Expedientes */}
          <GlassCard>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>
              Expedientes ({client.legal_files_count ?? 0})
            </Typography>
            <DatagridCustom
              rows={legalFilesRows}
              columns={legalFilesColumns}
              getRowId={(row) => row.id}
              loading={legalFilesLoading}
              paginationModel={legalFilesPagination}
              onPaginationChange={setLegalFilesPagination}
              total={legalFilesTotal}
              filterModel={legalFilesFilterModel}
              onFilterChange={handleLegalFilesFilterChange}
              filterMode="server"
              isServerPagination
              pageSizeOptions={[20, 50, 100]}
            />
          </GlassCard>
        </Box>
      )}
    </AppDialog>
  );
}
